import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../api/client'
import type { components } from '../../api/generated/schema'
import type { Envelope, Notice, NoticeLevel } from '../../lib/envelope'
import { patchSheets } from '../../lib/sheetCache'
import { sceneKeys } from './queries'
import type { SceneSections, SceneSheet } from './types'

type Schema = components['schemas']

export type SceneEnvelope = Envelope<SceneSections>

const NOTICE_LEVELS: NoticeLevel[] = ['info', 'warn', 'error']

function toNotice(raw: Schema['Notice']): Notice {
  const level = raw.level as NoticeLevel | undefined
  return {
    level: level !== undefined && NOTICE_LEVELS.includes(level) ? level : 'info',
    code: raw.code ?? 'unknown',
    message: raw.message ?? '',
  }
}

export function toEnvelope(dto: Schema['SceneEnvelope']): SceneEnvelope {
  return {
    version: dto.version ?? 0,
    changed: (dto.changed ?? {}) as Partial<SceneSections>,
    notices: (dto.notices ?? []).map(toNotice),
  }
}

export interface SceneMutationOptions<TVariables> {
  sceneId: number
  request: (variables: TVariables) => Promise<Schema['SceneEnvelope']>
  onEnvelope?: (envelope: SceneEnvelope) => void
  onFailure?: (error: unknown) => void
}

/**
 * The scene counterpart of `useSectionMutation`.
 *
 * Both of a scene's cache entries are patched, each filtered to what it holds — a cast
 * change answers with `characters` and, when a snapshot exists, the `snapshot` section
 * whose `characterCount` has just gone stale. The sheet entry takes the first, the
 * snapshot entry takes both, and neither invents a section its read would not return.
 *
 * Nothing optimistic. The genuinely fast-feeling parts of this screen — the exchange
 * counter, the turn marker — are device-local and never touch the network at all, which
 * is a better answer than guessing at a server response.
 */
export function useSceneMutation<TVariables>(options: SceneMutationOptions<TVariables>) {
  const queryClient = useQueryClient()

  return useMutation<SceneEnvelope, unknown, TVariables>({
    mutationFn: async (variables) => toEnvelope(await options.request(variables)),

    onError: (error) => {
      options.onFailure?.(error)
    },

    onSuccess: (envelope) => {
      patchSheets<SceneSections, SceneSheet>(
        queryClient,
        [sceneKeys.sheet(options.sceneId), sceneKeys.snapshot(options.sceneId)],
        envelope,
      )
      options.onEnvelope?.(envelope)
    },
  })
}

type Envelopes = Schema['SceneEnvelope']

export const sceneApi = {
  update: (sceneId: number, body: Schema['UpdateSceneRequest']) =>
    api.put<Envelopes>(`/scenes/${sceneId}`, body),

  // ----- aspects -----------------------------------------------------------

  addAspect: (sceneId: number, description: string) =>
    api.post<Envelopes>(`/scenes/${sceneId}/aspects`, { description }),

  updateAspect: (sceneId: number, aspectId: number, description: string) =>
    api.put<Envelopes>(`/scenes/${sceneId}/aspects/${aspectId}`, { description }),

  removeAspect: (sceneId: number, aspectId: number) =>
    api.delete<Envelopes>(`/scenes/${sceneId}/aspects/${aspectId}`),

  /** Must name every aspect exactly once; the API rejects a partial ordering with a 400. */
  reorderAspects: (sceneId: number, ids: number[]) =>
    api.put<Envelopes>(`/scenes/${sceneId}/aspects/order`, { ids }),

  // ----- cast --------------------------------------------------------------

  addCharacter: (sceneId: number, characterId: number, role: Schema['SceneCharacterType']) =>
    api.post<Envelopes>(`/scenes/${sceneId}/characters`, { characterId, role }),

  setCharacterRole: (
    sceneId: number,
    sceneCharacterId: number,
    role: Schema['SceneCharacterType'],
  ) => api.put<Envelopes>(`/scenes/${sceneId}/characters/${sceneCharacterId}`, { role }),

  removeCharacter: (sceneId: number, sceneCharacterId: number) =>
    api.delete<Envelopes>(`/scenes/${sceneId}/characters/${sceneCharacterId}`),

  // ----- panels ------------------------------------------------------------

  addPanel: (sceneId: number, description: string) =>
    api.post<Envelopes>(`/scenes/${sceneId}/panels`, { description }),

  updatePanel: (sceneId: number, panelId: number, description: string) =>
    api.put<Envelopes>(`/scenes/${sceneId}/panels/${panelId}`, { description }),

  removePanel: (sceneId: number, panelId: number) =>
    api.delete<Envelopes>(`/scenes/${sceneId}/panels/${panelId}`),

  reorderPanels: (sceneId: number, ids: number[]) =>
    api.put<Envelopes>(`/scenes/${sceneId}/panels/order`, { ids }),

  // ----- snapshot ----------------------------------------------------------

  /** Rebuilds the snapshot from the cast as it stands now. */
  regenerateSnapshot: (sceneId: number) =>
    api.post<Envelopes>(`/scenes/${sceneId}/snapshot`, undefined),

  setSnapshot: (sceneId: number, text: string) =>
    api.put<Envelopes>(`/scenes/${sceneId}/snapshot`, { text }),

  clearSnapshot: (sceneId: number) => api.delete<Envelopes>(`/scenes/${sceneId}/snapshot`),
}
